import type { NeoConversationError } from "../../../hexagon/core/ports/driver/neoConversationService";
import type { NeoConversationMessageDTO } from "../../../hexagon/core/ports/driven/neoConversationRepository";
import {
  DAILY_GRACE_EXHAUSTED_ERROR_CODE,
  DAILY_GRACE_EXHAUSTED_MESSAGE,
} from "../../../util/askForMoreCredits";
import type { CopilotMessage } from "./types";

// User-facing copy for failed turns, ported from the old Neo page so both
// surfaces say the same thing. Raw backend text (vendor names, status codes)
// never reaches the user except where the backend wrote it for them.

export type TurnNotice = Pick<
  CopilotMessage,
  "error" | "errorTitle" | "retryable" | "errorKind"
>;

const LENGTH_KIND_HINTS = [
  "context_length_exceeded",
  "conversation_length_exceeded",
  "max_tokens_exceeded",
  "token_limit_reached",
  "input_too_long",
];

const LENGTH_TEXT_HINTS = [
  "maximum conversation length",
  "conversation too long",
  "context length",
  "context window",
  "token limit",
  "too many tokens",
  "max tokens",
  "prompt is too long",
  "input is too long",
  "length exceeded",
];

/** Context/token-limit failures, where "Try again" can't help. */
export const isLengthLimitLike = (
  payload: { kind?: unknown; title?: unknown; message?: unknown } | null
): boolean => {
  if (!payload) return false;
  const text = (value: unknown) =>
    typeof value === "string" ? value.toLowerCase() : "";
  const kind = text(payload.kind);
  if (LENGTH_KIND_HINTS.some((hint) => kind.includes(hint))) return true;
  const haystack = `${kind} ${text(payload.title)} ${text(payload.message)}`;
  return LENGTH_TEXT_HINTS.some((hint) => haystack.includes(hint));
};

const LENGTH_LIMIT: TurnNotice = {
  errorTitle: "Maximum conversation length reached",
  error: "Please start a new conversation to continue.",
  retryable: false,
  errorKind: "length_limit",
};

const CAPACITY: TurnNotice = {
  errorTitle: "At capacity",
  error: "Neo was briefly busy. Please try that again in a moment.",
  retryable: true,
};

const GENERIC: TurnNotice = {
  errorTitle: "Couldn’t reply",
  error: "Neo couldn’t finish that response. Please try again.",
  retryable: true,
};

export const CONNECTION_INTERRUPTED: TurnNotice = {
  errorTitle: "Connection interrupted",
  error: "The response stopped before Neo finished. You can try again.",
  retryable: true,
};

/** A persisted assistant row that ended failed or cancelled. */
export const noticeForTerminalRow = (
  message: NeoConversationMessageDTO
): TurnNotice | null => {
  if (message.status === "cancelled") {
    return {
      errorTitle: "Response interrupted",
      error:
        "Neo didn’t finish that response. You can try again or pick up where you left off.",
      retryable: true,
    };
  }
  if (message.status !== "failed") return null;
  if (isLengthLimitLike(message.error_json ?? null)) return LENGTH_LIMIT;
  if (message.error_json?.kind === "provider_overloaded") return CAPACITY;
  return GENERIC;
};

/** The SSE ``error`` frame — the backend's ``friendly_error_payload``. */
export const noticeForStreamError = (
  payload: Record<string, unknown>
): TurnNotice => {
  if (isLengthLimitLike(payload)) return LENGTH_LIMIT;
  return {
    errorTitle:
      typeof payload.title === "string" ? payload.title : "Couldn’t finish",
    error:
      typeof payload.message === "string" ? payload.message : GENERIC.error,
    retryable:
      typeof payload.retryable === "boolean" ? payload.retryable : true,
  };
};

/** The turn request itself was refused (non-2xx before streaming). */
export const noticeForHttpError = (
  error: NeoConversationError | undefined
): TurnNotice => {
  if (error?.status === 413 || isLengthLimitLike(error ?? null)) {
    return LENGTH_LIMIT;
  }
  if (error?.status === 503) return CAPACITY;
  if (
    error?.status === 402 &&
    error.code === DAILY_GRACE_EXHAUSTED_ERROR_CODE
  ) {
    // Post-trial daily grace used up: use the "billing" kind so the upgrade CTA
    // button renders (trial_ended is text-only on this surface).
    return {
      errorTitle: "Daily free credits used up",
      error: DAILY_GRACE_EXHAUSTED_MESSAGE,
      retryable: false,
      errorKind: "billing",
    };
  }
  if (error?.status === 402 && error.code === "neo_trial_ended") {
    return {
      errorTitle: "Free trial ended",
      error:
        "Your clinic’s Neo free trial has ended. Upgrade to a paid plan to keep using Neo — any remaining balance is safe.",
      retryable: false,
      errorKind: "trial_ended",
    };
  }
  if (error?.status === 402) {
    return {
      errorTitle: "Out of Neo credits",
      error: "You’re out of Neo credits.",
      retryable: false,
      errorKind: "billing",
    };
  }
  return GENERIC;
};
